/**
 * Voicing a chord line in RUNS (Sep 2026, sections playing their own
 * instruments): a tune whose A sections comp with the left hand under a piano
 * melody and whose bridge comps with two hands is two different walks, one
 * per texture. Consecutive chords sharing a plan are voiced together, so the
 * walk still voice-leads inside a run; a change of plan starts a new walk,
 * which is what a change of texture is at the piano anyway.
 *
 * Pure and instrument-free: the caller says what a chord's plan is, how two
 * plans compare, and how to voice a run.
 */

/**
 * Voice `chords` run by run. A chord whose plan is null (a guitar section)
 * comes back null, and so does every chord of a run the voicer cannot answer:
 * those chords keep the grips they already carry, so a section never falls
 * silent. With one plan throughout this is exactly one call to `voice`.
 */
export function voiceByRuns<C, P, R>(
  chords: readonly C[],
  planOf: (chord: C, index: number) => P | null,
  keyOf: (plan: P) => string,
  voice: (run: C[], plan: P) => readonly R[] | null,
): (R | null)[] {
  const out: (R | null)[] = new Array(chords.length).fill(null);
  let start = 0;
  while (start < chords.length) {
    const plan = planOf(chords[start], start);
    const key = plan === null ? null : keyOf(plan);
    let end = start + 1;
    while (end < chords.length) {
      const next = planOf(chords[end], end);
      if ((next === null ? null : keyOf(next)) !== key) break;
      end += 1;
    }
    if (plan !== null) {
      const voiced = voice(chords.slice(start, end), plan);
      if (voiced && voiced.length === end - start) {
        voiced.forEach((chord, offset) => {
          out[start + offset] = chord;
        });
      }
    }
    start = end;
  }
  return out;
}
